
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, RotateCcw, Calendar as CalendarIcon, Plus, Info, Clock, Bug, AlertCircle } from 'lucide-react';
import { apiRequest } from '../services/api';
import { PublicData, GlobalSettings } from '../types';
import { WEEK_DAYS } from '../constants';
import { CalendarSkeleton } from './Skeletons';

interface CalendarViewProps { settings: GlobalSettings; }

export const CalendarView: React.FC<CalendarViewProps> = ({ settings }) => {
  const [data, setData] = useState<PublicData | null>(null);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const [showDebug, setShowDebug] = useState(false);
  const navigate = useNavigate();

  const loadData = async () => {
    setLoading(true);
    const res = await apiRequest<PublicData>('getPublic');
    if (res.success && res.data) {
      setData(res.data);
    }
    setLoading(false);
  };

  useEffect(() => { loadData(); }, []);

  const getLocalDateString = (d: Date) => {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };

  const todayStr = useMemo(() => getLocalDateString(new Date()), []);
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDay = new Date(year, month, 1).getDay();

  const maskName = (name: string) => {
    if (!name) return "";
    const n = name.toString().trim();
    if (n.length <= 1) return n;
    if (n.length === 2) return n[0] + "*";
    return n[0] + "*" + n[n.length - 1];
  };

  const checkIsBlocked = (dateStr: string) => {
    const targetDate = new Date(dateStr);
    targetDate.setHours(0, 0, 0, 0);
    const now = new Date();
    now.setHours(0, 0, 0, 0);

    const diffDays = Math.ceil((targetDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

    if (diffDays < settings.minNotice) return `需提前 ${settings.minNotice} 天`;
    if (diffDays > settings.maxAdvance) return `僅開放 ${settings.maxAdvance} 天內`;
    if (diffDays < 0) return '已過期';

    const day = targetDate.getDay();
    const configsToUse = settings.allConfigs.length > 0 ? settings.allConfigs : (data?.configs || []);

    for (const cfg of configsToUse) {
      const cfgVal = (cfg.value || "").trim();
      if (cfg.type === 'WEEKDAY' && cfgVal.split(',').map(v => v.trim()).includes(String(day))) return cfg.note || '例行公休';
      if (cfg.type === 'DATE' && cfgVal === dateStr) return cfg.note || '特定公休';
      if (cfg.type === 'RANGE') {
        const [start, end] = cfgVal.split(',');
        if (dateStr >= (start || "").trim() && dateStr <= (end || "").trim()) return cfg.note || '期間休假';
      }
    }
    return null;
  };

  if (loading && !data) return <CalendarSkeleton />;

  const cells = [];
  for (let i = 0; i < firstDay; i++) {
    cells.push(<div key={`empty-${i}`} className="min-h-[90px] sm:h-32 bg-slate-50 border-r border-b border-slate-100 opacity-50"></div>);
  }

  for (let d = 1; d <= daysInMonth; d++) {
    const dStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    const blockReason = checkIsBlocked(dStr);
    
    const dayBookings = data?.bookings.filter(b => {
      const bDate = (b.date || "").toString().trim();
      const bStatus = (b.status || "").toString().toUpperCase();
      return bDate === dStr && bStatus !== 'REJECTED';
    }) || [];

    const isToday = todayStr === dStr;

    cells.push(
      <div key={d} className={`min-h-[140px] sm:h-44 p-1 sm:p-2 border-r border-b border-slate-100 relative bg-white transition-all overflow-hidden ${blockReason ? 'bg-slate-50/70' : 'hover:bg-blue-50/10'}`}>
        <div className="flex justify-between items-start mb-1">
          <div className={`text-sm sm:text-base font-black w-7 h-7 sm:w-8 h-8 flex items-center justify-center rounded-full ${isToday ? 'bg-blue-600 text-white shadow-lg ring-4 ring-blue-50' : 'text-slate-700'}`}>
            {d}
          </div>
          {!blockReason && (
            <button 
              onClick={() => navigate('/book', { state: { date: dStr } })} 
              className="bg-blue-600 text-white p-1.5 sm:p-2 rounded-xl shadow-md z-20 hover:bg-blue-700 active:scale-90 transition-all"
            >
              <Plus className="w-3.5 h-3.5 sm:w-4 h-4" />
            </button>
          )}
        </div>

        <div className="flex flex-col gap-1.5">
          {blockReason && (
            <div className={`text-[9px] sm:text-[10px] px-1.5 py-1 rounded-lg text-center font-black ${blockReason.includes('天') ? 'bg-slate-100 text-slate-400' : 'bg-rose-100 text-rose-500 border border-rose-200'}`}>
              {blockReason}
            </div>
          )}
          
          <div className="w-full space-y-1 mt-1 max-h-[6rem] overflow-y-auto scrollbar-hide">
            {dayBookings.sort((a,b) => (a.time || "").localeCompare(b.time || "")).map((b, idx) => (
              <div key={idx} className={`text-[9px] sm:text-[11px] px-2 py-1.5 rounded-xl border shadow-sm flex items-center justify-between gap-1 transition-all hover:scale-[1.02] ${b.status === 'CONFIRMED' ? 'bg-emerald-50 text-emerald-700 border-emerald-100' : 'bg-amber-50 text-amber-700 border-amber-100'}`}>
                <span className="font-black whitespace-nowrap">{ (b.time || "").slice(0, 5) }</span>
                <span className="truncate font-bold opacity-80">{maskName(b.name)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-4xl mx-auto space-y-8 animate-fade-in pb-12">
      <div className="text-center pt-8">
        <h1 className="text-4xl sm:text-5xl font-black text-slate-800 tracking-tighter cursor-help" onClick={() => setShowDebug(!showDebug)}>
          {settings.formTitle}
        </h1>
        <div className="w-24 h-2 bg-blue-600 mx-auto mt-6 rounded-full shadow-lg shadow-blue-100"></div>
      </div>

      <div className="bg-white rounded-[3rem] shadow-2xl border border-slate-200 overflow-hidden">
        <div className="p-8 flex flex-col sm:flex-row justify-between items-center bg-white border-b border-slate-100 gap-6">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-blue-600 text-white rounded-2xl shadow-xl shadow-blue-100">
              <CalendarIcon className="w-7 h-7" />
            </div>
            <div>
              <h2 className="text-2xl sm:text-3xl font-black text-slate-800">{year}年 {month + 1}月</h2>
              <p className="text-[11px] text-slate-400 font-black uppercase tracking-[0.2em]">{settings.siteTitle}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex bg-slate-100 rounded-2xl p-1.5 shadow-inner">
              <button onClick={() => setCurrentDate(new Date(year, month - 1, 1))} className="p-2.5 hover:bg-white hover:shadow-md rounded-xl transition-all text-slate-600 active:scale-95">
                <ChevronLeft className="w-6 h-6" />
              </button>
              <button onClick={() => setCurrentDate(new Date(year, month + 1, 1))} className="p-2.5 hover:bg-white hover:shadow-md rounded-xl transition-all text-slate-600 active:scale-95">
                <ChevronRight className="w-6 h-6" />
              </button>
            </div>
            <button onClick={loadData} className="p-4 bg-white border border-slate-200 rounded-2xl text-slate-500 hover:bg-slate-50 transition-all shadow-sm active:rotate-180">
              <RotateCcw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
        
        <div className="grid grid-cols-7 text-center text-[12px] font-black bg-slate-50/50 border-b border-slate-100">
          {WEEK_DAYS.map(day => (
            <div key={day} className={`py-5 text-slate-400 uppercase tracking-widest ${day === '日' || day === '六' ? 'text-rose-400' : ''}`}>{day}</div>
          ))}
        </div>
        
        <div className="grid grid-cols-7 bg-white">{cells}</div>
        
        <div className="px-8 py-10 bg-slate-50/30 border-t border-slate-100 space-y-8">
          <div className="flex flex-wrap gap-x-10 gap-y-6 text-[11px] font-black text-slate-400 uppercase tracking-widest">
            <div className="flex items-center gap-3">
              <span className="w-5 h-5 rounded-lg border-2 border-slate-200 bg-white shadow-sm"></span>
              <span>開放時段</span>
            </div>
            <div className="flex items-center gap-3">
              <span className="w-5 h-5 rounded-lg bg-emerald-50 border border-emerald-100 shadow-sm"></span>
              <span className="text-emerald-600">已確認預約</span>
            </div>
            <div className="flex items-center gap-3">
              <span className="w-5 h-5 rounded-lg bg-amber-50 border border-amber-100 shadow-sm"></span>
              <span className="text-amber-600">待審核預約</span>
            </div>
          </div>
          
          <div className="flex items-center gap-3 text-sm text-blue-600 bg-blue-50/80 px-5 py-4 rounded-2xl font-black border border-blue-100">
            <Clock className="w-5 h-5 shrink-0" />
            <span>開放預約範圍：{settings.minNotice} 天前 ~ {settings.maxAdvance} 天內。</span>
          </div>
        </div>
      </div>

      {/* 偵錯面板 */}
      {showDebug && (
        <div className="mt-12 bg-slate-900 text-slate-300 p-8 rounded-[2.5rem] shadow-2xl font-mono text-[11px] overflow-hidden border-4 border-emerald-500/20">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-white font-black flex items-center gap-2 uppercase tracking-widest">
              <Bug className="w-4 h-4 text-emerald-400" /> [偵錯資訊] 目前抓取到的預約數據
            </h3>
            <span className="bg-emerald-500/10 text-emerald-400 px-2 py-0.5 rounded text-[10px]">TOTAL: {data?.bookings.length || 0}</span>
          </div>
          <div className="bg-amber-900/20 border border-amber-500/30 p-4 rounded-xl mb-4 text-amber-200">
            <p className="flex items-center gap-2 font-black mb-1 text-[12px]"><AlertCircle className="w-4 h-4" /> 注意：</p>
            <p>試算表中的日期必須符合目前日曆的月份。如果您目前的日曆是 **3月**，但試算表填的是 **12月**，資料是不會顯示在畫面上的。</p>
          </div>
          <div className="max-h-[300px] overflow-y-auto space-y-2 scrollbar-hide border-t border-slate-800 pt-4">
            {data?.bookings && data.bookings.length > 0 ? (
              data.bookings.map((b, idx) => (
                <div key={idx} className="flex flex-wrap gap-4 border-b border-slate-800 pb-1 hover:bg-slate-800/50">
                  <span className="text-slate-500 w-4">{idx + 1}.</span>
                  <span className={b.date.includes('-12-') ? 'text-rose-400 font-black underline' : 'text-emerald-400'}>日期: {b.date}</span>
                  <span className="text-blue-400">時段: {b.time}</span>
                  <span className="text-slate-100 truncate max-w-[100px]">姓名: {b.name}</span>
                  <span className="text-amber-400">電話/Email: {b.email}</span>
                  <span className={`text-[9px] px-1 rounded ${b.status === 'CONFIRMED' ? 'bg-emerald-900 text-emerald-300' : 'bg-amber-900 text-amber-300'}`}>{b.status}</span>
                </div>
              ))
            ) : (
              <div className="text-rose-400 font-black">目前沒有從 Google 試算表抓取到任何資料。</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
